import { Select, TextArea, TextInput, Toggle } from '../../components/ui';
import type { CustomerFormAppField } from '../../types';

/**
 * Renders the one input a custom field's type asks for — the same control
 * set the customer form uses. Shared by the lead create dialog and the
 * lead detail editor. Photo and geo are handled by the phone, not here.
 */
export function LeadFieldInput({
  field, value, onChange,
}: {
  field: CustomerFormAppField;
  value: unknown;
  onChange: (value: unknown) => void;
}) {
  const label = field.required ? `${field.label} *` : field.label;
  const hint = field.helpText;

  switch (field.type) {
    case 'textarea':
      return (
        <TextArea label={label} hint={hint} rows={3} placeholder={field.placeholder}
          value={(value as string) ?? ''} onChange={(e) => onChange(e.target.value)} />
      );
    case 'number':
      return (
        <TextInput label={label} hint={hint} type="number" placeholder={field.placeholder}
          value={(value as string) ?? ''} onChange={(e) => onChange(e.target.value)} />
      );
    case 'date':
      return (
        <TextInput label={label} hint={hint} type="date"
          value={(value as string) ?? ''} onChange={(e) => onChange(e.target.value)} />
      );
    case 'email':
      return (
        <TextInput label={label} hint={hint} type="email" placeholder={field.placeholder}
          value={(value as string) ?? ''} onChange={(e) => onChange(e.target.value)} />
      );
    case 'boolean':
      return (
        <div className="field">
          <Toggle checked={Boolean(value)} label={label} onChange={(c) => onChange(c)} />
          {hint ? <span className="field-hint">{hint}</span> : null}
        </div>
      );
    case 'select':
      return (
        <Select label={label} hint={hint} value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value)}>
          <option value="">— Select —</option>
          {(field.options ?? []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </Select>
      );
    case 'multiselect': {
      const selected = Array.isArray(value) ? (value as string[]) : [];
      return (
        <div className="field">
          <label>{label}</label>
          <div className="col gap-4">
            {(field.options ?? []).map((o) => (
              <label key={o.value} className="row gap-8" style={{ alignItems: 'center' }}>
                <input type="checkbox" checked={selected.includes(o.value)}
                  onChange={(e) => onChange(e.target.checked
                    ? [...selected, o.value]
                    : selected.filter((v) => v !== o.value))} />
                <span>{o.label}</span>
              </label>
            ))}
          </div>
          {hint ? <span className="field-hint">{hint}</span> : null}
        </div>
      );
    }
    default:
      return (
        <TextInput label={label} hint={hint} placeholder={field.placeholder}
          className={field.type === 'gstin' || field.type === 'pan' ? 'mono' : undefined}
          value={(value as string) ?? ''} onChange={(e) => onChange(e.target.value)} />
      );
  }
}

/** Fields the desk renders — everything but the phone-only photo and pin. */
export const deskFields = (fields: CustomerFormAppField[]) =>
  fields.filter((f) => !f.system && f.type !== 'photo' && f.type !== 'geo');

export function isFilled(field: CustomerFormAppField, value: unknown): boolean {
  if (field.type === 'boolean') return true;
  if (field.type === 'multiselect') return Array.isArray(value) && value.length > 0;
  return value != null && String(value).trim() !== '';
}
